/**
 * Client-side validation for a role's screening config, run before the PUT so
 * every message sits under its field. Mirrors what the worker's Zod rejects.
 */
import { RULE_OPS, type RuleOp, type RuleValue, type ScreeningConfig, type ScreeningQuestion } from './types';

export const KEY_RE = /^[a-z][a-z0-9_]*$/;
const NUMERIC_OPS: ReadonlySet<RuleOp> = new Set(['gt', 'gte', 'lt', 'lte']);

export interface QuestionErrors {
  key?: string;
  prompt?: string;
  choices?: string;
}

export interface RuleErrors {
  field?: string;
  value?: string;
  reason?: string;
  points?: string;
}

export interface ConfigErrors {
  /** Indexed by question position. */
  questions: Record<number, QuestionErrors>;
  knockouts: Record<number, RuleErrors>;
  scoring: Record<number, RuleErrors>;
  qualified_min?: string;
  review_min?: string;
}

export const NO_CONFIG_ERRORS: ConfigErrors = { questions: {}, knockouts: {}, scoring: {} };

export function hasConfigErrors(e: ConfigErrors): boolean {
  return (
    Object.keys(e.questions).length > 0 ||
    Object.keys(e.knockouts).length > 0 ||
    Object.keys(e.scoring).length > 0 ||
    !!e.qualified_min ||
    !!e.review_min
  );
}

export function opNeedsValue(op: RuleOp): boolean {
  return RULE_OPS.find((o) => o.value === op)?.needsValue ?? true;
}

function valueMissing(v: RuleValue): boolean {
  return v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function ruleErrors(
  r: { field: string; op: RuleOp; value: RuleValue },
  keys: Set<string>,
  questions: ScreeningQuestion[],
): RuleErrors {
  const e: RuleErrors = {};
  if (!r.field) e.field = 'Pick a question.';
  else if (!keys.has(r.field)) e.field = 'This question no longer exists.';
  if (opNeedsValue(r.op)) {
    if (valueMissing(r.value)) e.value = 'Enter a value.';
    else if (NUMERIC_OPS.has(r.op) || questions.find((q) => q.key === r.field)?.type === 'number') {
      const n = Array.isArray(r.value) ? NaN : Number(r.value);
      if (!Array.isArray(r.value) && !Number.isFinite(n)) e.value = 'Enter a number.';
    }
  }
  return e;
}

export function validateConfig(c: ScreeningConfig): ConfigErrors {
  const errors: ConfigErrors = { questions: {}, knockouts: {}, scoring: {} };
  const seen = new Map<string, number>();
  c.questions.forEach((q, i) => {
    const e: QuestionErrors = {};
    const key = q.key.trim();
    if (!key) e.key = 'Enter a key.';
    else if (!KEY_RE.test(key)) e.key = 'Lowercase letters, digits and underscores; start with a letter.';
    else if (seen.has(key)) e.key = `Already used by question ${seen.get(key)! + 1}.`;
    else seen.set(key, i);
    if (!q.prompt.trim()) e.prompt = 'Enter the question the assistant asks.';
    if (q.type === 'choice' && (q.choices ?? []).filter((x) => x.trim()).length < 2) e.choices = 'List at least two choices, separated by commas.';
    if (Object.keys(e).length) errors.questions[i] = e;
  });

  const keys = new Set(c.questions.map((q) => q.key));
  c.rules.knockouts.forEach((k, i) => {
    const e = ruleErrors(k, keys, c.questions);
    if (!k.reason.trim()) e.reason = 'Say why this rules a candidate out — it shows on the card.';
    if (Object.keys(e).length) errors.knockouts[i] = e;
  });
  c.rules.scoring.forEach((s, i) => {
    const e = ruleErrors(s, keys, c.questions);
    if (!Number.isFinite(s.points) || s.points === 0) e.points = 'Enter points other than 0.';
    if (Object.keys(e).length) errors.scoring[i] = e;
  });

  if (!Number.isFinite(c.rules.qualified_min)) errors.qualified_min = 'Enter a number.';
  if (!Number.isFinite(c.rules.review_min)) errors.review_min = 'Enter a number.';
  if (!errors.qualified_min && !errors.review_min && c.rules.review_min > c.rules.qualified_min) {
    errors.review_min = 'Review mark must be at or below the qualified mark.';
  }
  return errors;
}

/** A fresh question with a key derived from the prompt, unique within the list. */
export function keyFromPrompt(prompt: string, taken: Iterable<string>): string {
  const base =
    prompt.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 40) || 'question';
  const used = new Set(taken);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}
